import { Button } from "@arcadeai/design-system/components/ui/atoms/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@arcadeai/design-system/components/ui/atoms/select";
import { PREBUILT_OAUTH_PROVIDERS } from "@arcadeai/design-system/metadata/oauth-providers";
import type { OAuthProviderIdType } from "@arcadeai/design-system/metadata/types";
import {
  findPrebuiltProviderById,
  findPrebuiltProviderByProviderId,
} from "@arcadeai/design-system/metadata/utils";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@arcadeai/design-system/rhf";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { type Resolver, useForm } from "react-hook-form";
import { toast } from "sonner";
import { useComputedRedirectUri } from "@/app/oauth/api/get-callback-url-template";
import {
  useCreateOAuthProvider,
  useGetOAuthProviders,
  useUpdateOAuthProvider,
} from "@/app/oauth/api/hooks";
import { DocsAlert } from "@/app/oauth/list/components/docs-alert";
import type {
  AuthProviderPrebuilt,
  OAuthProviderItem,
} from "@/app/oauth/types";
import { AuthProviderSchemaPrebuilt } from "@/app/oauth/types";
import { RedirectUriEditField } from "../redirect-uri-edit-field";
import { RedirectUriField } from "../redirect-uri-field";
import { ClientIdField } from "./fields/client-id-field";
import { ClientSecretField } from "./fields/client-secret-field";
import { DescriptionField } from "./fields/description-field";
import { IdField } from "./fields/id-field";

type PrebuiltFormProps = {
  oauth?: OAuthProviderItem;
  handleClose: () => void;
  freshExternalId: string;
};

export const PrebuiltForm = ({
  oauth,
  handleClose,
  freshExternalId: providedExternalId,
}: PrebuiltFormProps) => {
  const isEditing = !!oauth;
  const isStatic = oauth?.binding.type === "static";
  const canUpdate = oauth?.binding.type !== "static";

  const { data: existingProviders = [] } = useGetOAuthProviders();
  const { redirectUri: computedRedirectUri, isLoading: configLoading } =
    useComputedRedirectUri(isEditing ? "" : providedExternalId);

  // Local edit-only toggle state
  const [useLegacyRedirectUri, setUseLegacyRedirectUri] = useState<boolean>(
    oauth?.oauth2.use_legacy_redirect_uri ?? false
  );

  const form = useForm<AuthProviderPrebuilt>({
    // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
    resolver: zodResolver(
      AuthProviderSchemaPrebuilt
    ) as unknown as Resolver<AuthProviderPrebuilt>,
    defaultValues: oauth
      ? {
          id: oauth.id,
          // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
          provider_id: oauth.provider_id as OAuthProviderIdType,
          status: "active",
          binding: { type: oauth.binding?.type || "project" },
          oauth2: {
            client_id: oauth.oauth2.client_id,
            client_secret: "", // Keep empty for editing - only update if user enters new value
          },
          description:
            oauth.description ??
            findPrebuiltProviderById(oauth.id)?.description,
          external_id: "", // Not used when editing, but required by schema
        }
      : {
          provider_id: PREBUILT_OAUTH_PROVIDERS[0].provider_id,
          description: PREBUILT_OAUTH_PROVIDERS[0].description,
          id: "",
          status: "active",
          binding: { type: "project" },
          oauth2: {
            client_id: "",
            client_secret: "",
          },
          external_id: providedExternalId,
        },
  });

  const selectedProviderId = form.watch("provider_id");
  const currentBindingType = form.watch("binding.type") ?? "project";
  const hasDuplicateProvider =
    !isEditing &&
    existingProviders.some(
      (provider) =>
        provider.provider_id === selectedProviderId &&
        provider.binding.type === currentBindingType
    );

  const { mutateAsync: createOAuthProvider } = useCreateOAuthProvider();
  const { mutateAsync: updateOAuthProvider } = useUpdateOAuthProvider({
    oauthId: oauth?.id ?? "",
    binding: currentBindingType,
  });

  async function handleSubmit(values: AuthProviderPrebuilt) {
    if (hasDuplicateProvider) {
      toast.error("You already have this provider set up.");
      return;
    }

    const clientSecret = values.oauth2.client_secret?.trim() ?? "";
    if (!isEditing && clientSecret === "") {
      form.setError("oauth2.client_secret", {
        message: "Client Secret is required",
      });
      toast.error("Client Secret is required");
      return;
    }

    try {
      if (isEditing) {
        const { client_secret: _clientSecret, ...oauth2WithoutSecret } =
          values.oauth2;
        const oauth2Updates =
          clientSecret === ""
            ? {
                ...oauth2WithoutSecret,
                use_legacy_redirect_uri: useLegacyRedirectUri,
              }
            : {
                ...oauth2WithoutSecret,
                client_secret: clientSecret,
                use_legacy_redirect_uri: useLegacyRedirectUri,
              };

        await updateOAuthProvider({
          ...values,
          oauth2: oauth2Updates,
        });
      } else {
        await createOAuthProvider(values);
      }

      handleClose();
    } catch {
      toast.error("Unable to save OAuth provider - please try again");
    }
  }

  function handleError(_error: unknown) {
    toast.error("Some required information is missing or incorrect");
  }

  return (
    <Form {...form}>
      <form
        className="mt-4 space-y-6"
        onSubmit={form.handleSubmit(handleSubmit, handleError)}
      >
        <div className="space-y-4">
          <FormField
            control={form.control}
            name="provider_id"
            render={({ field }) => {
              const provider =
                findPrebuiltProviderByProviderId(field.value ?? "") ??
                PREBUILT_OAUTH_PROVIDERS[0];
              return (
                <FormItem>
                  <FormLabel>Provider</FormLabel>
                  <Select
                    {...field}
                    disabled={isStatic || isEditing}
                    onValueChange={(value) => {
                      if (value == null) {
                        return;
                      }
                      // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
                      field.onChange(value as OAuthProviderIdType);
                      const description =
                        findPrebuiltProviderByProviderId(value)?.description ??
                        "";
                      form.setValue("description", description);
                    }}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select a provider" />
                    </SelectTrigger>
                    <SelectContent>
                      {PREBUILT_OAUTH_PROVIDERS.map((prebuiltProvider) => {
                        const isDuplicateProvider =
                          !isEditing &&
                          existingProviders.some(
                            (existing) =>
                              existing.provider_id ===
                                prebuiltProvider.provider_id &&
                              existing.binding.type === currentBindingType
                          );

                        return (
                          <SelectItem
                            disabled={isDuplicateProvider}
                            key={prebuiltProvider.provider_id}
                            value={prebuiltProvider.provider_id}
                          >
                            <div className="flex items-center gap-4">
                              <prebuiltProvider.icon className="size-4" />
                              {prebuiltProvider.name}
                              {!!isDuplicateProvider && (
                                <span className="text-muted-foreground text-xs">
                                  (Already set up)
                                </span>
                              )}
                            </div>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                  <DocsAlert provider={provider} />
                  {!!hasDuplicateProvider && (
                    <FormMessage>
                      You already have this provider set up.
                    </FormMessage>
                  )}
                </FormItem>
              );
            }}
          />
          <IdField canUpdate={canUpdate} form={form} />
          <ClientIdField canUpdate={canUpdate} form={form} />
          <ClientSecretField canUpdate={canUpdate} form={form} oauth={oauth} />
          {!isEditing && (
            <RedirectUriField
              isLoading={configLoading}
              redirectUri={computedRedirectUri}
            />
          )}
          {!!(isEditing && oauth) && (
            <RedirectUriEditField
              allowLegacy={oauth.oauth2.allow_legacy_redirect_uri}
              defaultRedirectUri={oauth.oauth2.redirect_uri}
              isLoading={false}
              legacyRedirectUri={oauth.oauth2.legacy_redirect_uri}
              onChangeUseLegacy={setUseLegacyRedirectUri}
              useLegacy={useLegacyRedirectUri}
            />
          )}
          <DescriptionField canUpdate={canUpdate} form={form} />
        </div>
        <Button
          className="w-full"
          disabled={!canUpdate || hasDuplicateProvider}
          type="submit"
        >
          {isEditing ? "Update" : "Create"}
        </Button>
      </form>
    </Form>
  );
};
